import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Botao, Cartao, EstadoCarregando, Selo } from '../../componentes/ui';
import { EnvioDeSped } from '../../componentes/EnvioDeSped';
import { LancamentoDeValores } from '../../componentes/LancamentoDeValores';
import { requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';
import { MinutaDeContrato } from '../MinutaDeContrato';
import { AssessorDoCliente } from './AssessorDoCliente';
import { ConfiguracaoDeCredito, rotuloDoTipo, type Settings } from './ConfiguracaoDeCredito';

/**
 * Serviços contratados pela empresa, com a configuração de papéis.
 *
 * ## O que o interruptor liga
 *
 * "Créditos ICMS" não é um campo booleano: ligar significa dizer **em que papel**
 * a empresa entra na operação — cliente, assessor, fornecedor — e, sendo
 * cliente, com quais pares (assessor, fornecedor) ela se vincula (RN-015).
 *
 * Por isso o interruptor abre a configuração em vez de gravar direto. No Deca 1
 * eram duas coisas separadas (o toggle e o botão "Configurações") e dava para
 * ligar o serviço sem escolher papel nenhum — o que grava um cliente que não
 * aparece em listagem alguma.
 *
 * ## Desligar tem consequência que a tela precisa dizer
 *
 * Tirar a empresa da operação **não** cancela contrato em andamento: ele
 * continua existindo, com cronograma disparando. A confirmação diz isso.
 *
 * ## A porta de entrada do cliente
 *
 * As abas de solução do perfil (ICMS Recorrente, ICMS Passivos) só existem
 * quando há dado. Um cliente recém-cadastrado abre aqui — e é daqui que sai o
 * primeiro SPED, pelo mesmo fluxo de envio da aba de Recorrente. Sem este
 * botão, o cliente sem dado não teria caminho para ganhar a aba.
 */

/**
 * Os serviços de negociação de Créditos ICMS — Recorrente, Pontual e
 * Passivos —, pelo nome do catálogo. Também o nome antigo, "Créditos ICMS",
 * de antes de o catálogo ganhar as outras frentes.
 */
export function ehServicoDeCreditosIcms(nome: string): boolean {
  return /^cr[ée]ditos\s+icms\b/i.test(nome.trim());
}

interface ServicoDaEmpresa {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly isActive: boolean;
  readonly liberado: boolean;
}

export function AbaServicos(props: {
  readonly businessId: string;
  /**
   * O perfil sabe se o cliente já tem SPED ou contrato de Recorrente; aqui isso
   * só muda o texto do cartão de envio — "comece por aqui" ou "os arquivos
   * estão na aba de Recorrente".
   */
  readonly semOperacaoRecorrente?: boolean;
}): JSX.Element {
  const [configAberta, setConfigAberta] = useState(false);
  const [envioAberto, setEnvioAberto] = useState(false);
  const [lancamentoAberto, setLancamentoAberto] = useState(false);
  // Com um modelo de contrato aberto no editor, a aba é só o editor.
  const [editandoModelo, setEditandoModelo] = useState(false);

  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['settings', props.businessId],
    queryFn: () => requisicaoAutenticada<Settings>(`/icms/settings/business/${props.businessId}`),
  });

  // O catálogo real, com o que esta empresa tem liberado. Antes as duas linhas
  // eram fixas no código — "Oportunidades tributárias" aparecia sempre inativa,
  // mesmo para quem a tem contratada.
  const servicos = useQuery({
    queryKey: ['servicos-da-empresa', props.businessId],
    queryFn: () =>
      requisicaoAutenticada<ServicoDaEmpresa[]>(`/business/${props.businessId}/servicos`),
  });

  const definir = useMutation({
    mutationFn: (entrada: { serviceId: string; liberado: boolean }) =>
      requisicaoAutenticada<ServicoDaEmpresa[]>(`/business/${props.businessId}/servicos`, {
        metodo: 'PUT',
        corpo: entrada,
      }),
    onSuccess: (lista) => {
      queryClient.setQueryData(['servicos-da-empresa', props.businessId], lista);
    },
  });

  if (settings.isLoading) return <EstadoCarregando contexto="os serviços" />;

  const papeis = settings.data?.papeis;
  const ativo =
    papeis?.cliente === true || papeis?.assessor === true || papeis?.fornecedor === true;

  return (
    <div className="flex flex-col gap-6">
      {/* RN-621 — quem assessora o cliente, antes de qualquer contrato. */}
      {!editandoModelo && papeis?.cliente === true && settings.data !== undefined && (
        <AssessorDoCliente businessId={props.businessId} settings={settings.data} />
      )}

      <section className={`flex min-w-0 flex-col gap-4${editandoModelo ? ' hidden' : ''}`}>
        <h2 className="font-display text-lg font-semibold tracking-tight">Serviços ativos</h2>

        <div className="overflow-hidden rounded-painel border border-borda bg-superficie-elevada shadow-cartao">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-superficie-tabelaTopo text-xs uppercase tracking-wide text-conteudo-tabelaTopo">
                <th scope="col" className="px-5 py-4 font-medium">
                  Serviço
                </th>
                <th scope="col" className="px-5 py-4 font-medium">
                  Situação
                </th>
                <th scope="col" className="px-5 py-4 text-right font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(servicos.data ?? []).map((s) => {
                // Os papéis (cliente, assessor, fornecedor) são da empresa e
                // valem para os três serviços de Créditos ICMS: o Pontual e o
                // Passivos também os exigem — o comprador é Cliente, quem vende
                // é Fornecedor. Por isso o botão aparece nos três e abre a
                // mesma configuração.
                const ehIcms = ehServicoDeCreditosIcms(s.name);
                return (
                  <tr key={s.id} className="border-b border-borda last:border-0">
                    <th scope="row" className="px-5 py-4 font-normal text-conteudo-primario">
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          checked={s.liberado}
                          disabled={definir.isPending}
                          onChange={(e) =>
                            definir.mutate({ serviceId: s.id, liberado: e.target.checked })
                          }
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
                        />
                        <span className="flex flex-col">
                          <span>{s.name}</span>
                          {s.description !== null && (
                            <span className="mt-0.5 max-w-xl text-xs font-normal text-conteudo-sutil">
                              {s.description}
                            </span>
                          )}
                        </span>
                      </label>
                    </th>
                    <td className="px-5 py-4 align-top">
                      <Selo tom={s.liberado ? 'positivo' : 'neutro'}>
                        {s.liberado ? 'Contratado' : 'Não contratado'}
                      </Selo>
                      {ehIcms && s.liberado && !ativo && (
                        <p className="mt-1 text-xs text-atencao">Sem papel definido</p>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right align-top">
                      {ehIcms ? (
                        <Botao
                          variante="secundario"
                          tamanho="pequeno"
                          onClick={() => setConfigAberta(true)}
                        >
                          Configurar papéis
                        </Botao>
                      ) : (
                        <span className="text-xs text-conteudo-sutil">Sem configuração</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {definir.isError && (
          <ErroDaApi titulo="Não foi possível alterar o serviço" erro={definir.error} />
        )}

        {ativo && papeis !== undefined && (
          <div className="flex flex-wrap gap-2">
            {papeis.cliente && <Selo tom="informativo">Cliente</Selo>}
            {papeis.assessor && <Selo tom="informativo">Assessor</Selo>}
            {papeis.fornecedor && <Selo tom="informativo">Fornecedor</Selo>}
          </div>
        )}

        {/*
          Os créditos configurados aparecem na aba, não só dentro do modal.
          É o que o escritório confere de relance — que tipos este fornecedor
          vende, por quanto, e quando ele recebe.
        */}
        {papeis?.fornecedor === true && (settings.data?.creditos.length ?? 0) > 0 && (
          <div className="overflow-hidden rounded-painel border border-borda">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Tipos de crédito do fornecedor</caption>
              <thead>
                <tr className="bg-superficie-tabelaTopo text-xs uppercase tracking-wide text-conteudo-tabelaTopo">
                  <th scope="col" className="px-5 py-3 font-medium">
                    Tipo de crédito
                  </th>
                  <th scope="col" className="px-5 py-3 text-right font-medium">
                    Habilitado
                  </th>
                  <th scope="col" className="px-5 py-3 text-right font-medium">
                    Deságio
                  </th>
                  <th scope="col" className="px-5 py-3 text-right font-medium">
                    Vencimento
                  </th>
                </tr>
              </thead>
              <tbody>
                {(settings.data?.creditos ?? []).map((c) => (
                  <tr key={c.tipoCredito} className="border-t border-borda">
                    <th
                      scope="row"
                      className="px-5 py-3 text-left font-normal text-conteudo-primario"
                    >
                      {rotuloDoTipo(c.tipoCredito)}
                    </th>
                    <td className="valor-fiscal px-5 py-3 text-right">
                      {c.credito.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </td>
                    <td className="valor-fiscal px-5 py-3 text-right">
                      {c.desagioPercentual.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}
                      %
                    </td>
                    <td className="valor-fiscal px-5 py-3 text-right text-conteudo-secundario">
                      {c.diaVencimento === null
                        ? `dia ${settings.data?.diaPagamento ?? '—'}`
                        : `dia ${c.diaVencimento}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/*
        Os arquivos de SPED moram na aba ICMS Recorrente, em "Arquivos": é lá
        que estão a lista por competência e o "Enviar SPED". Aqui fica só a
        porta de entrada de quem ainda **não tem** essa aba — o cliente recém
        cadastrado, que sem este cartão não teria por onde mandar o primeiro
        arquivo. Assim que o primeiro entra, a aba nasce e este cartão some.
      */}
      {!editandoModelo && papeis?.cliente === true && props.semOperacaoRecorrente === true && (
        <Cartao
          como="section"
          titulo="Primeiro SPED deste cliente"
          subtitulo="Este cliente ainda não tem cálculo. Envie o SPED Fiscal: o sistema apura o crédito por competência e a aba ICMS Recorrente passa a existir, com os arquivos e os relatórios."
          acao={
            <div className="flex flex-wrap gap-2">
              {/* RN-085 — sem o arquivo em mãos, a apuração entra pelo valor. */}
              <Botao
                variante="secundario"
                tamanho="pequeno"
                onClick={() => setLancamentoAberto(true)}
              >
                Lançar valores
              </Botao>
              <Botao tamanho="pequeno" onClick={() => setEnvioAberto(true)}>
                Enviar o primeiro SPED
              </Botao>
            </div>
          }
        >
          <p className="text-xs text-conteudo-sutil">
            Um arquivo .txt por competência, até 24 por envio — o contribuinte é conferido pelo CNPJ
            do registro 0000. Se o contador só mandou os valores, "Lançar valores" faz a mesma conta
            pelo ICMS apurado de cada mês.
          </p>
        </Cartao>
      )}

      {/*
        Os modelos de contrato moram aqui, e não numa aba própria: são dos
        serviços prestados (lista de 21/09). Um ou mais por serviço; no
        fechamento, quando as partes têm modelo, quem fecha escolhe.
      */}
      <MinutaDeContrato businessId={props.businessId} aoMudarEdicao={setEditandoModelo} />

      {settings.data !== undefined && (
        <ConfiguracaoDeCredito
          businessId={props.businessId}
          atual={settings.data}
          aberto={configAberta}
          aoFechar={() => setConfigAberta(false)}
        />
      )}

      <EnvioDeSped
        clienteId={props.businessId}
        aberto={envioAberto}
        aoFechar={() => setEnvioAberto(false)}
      />

      <LancamentoDeValores
        clienteId={props.businessId}
        aberto={lancamentoAberto}
        aoFechar={() => setLancamentoAberto(false)}
      />
    </div>
  );
}

/** Interruptor com rótulo clicável — um `checkbox` estilizado, não um `div`. */
function Interruptor(props: {
  readonly rotulo: string;
  readonly ajuda: string;
  readonly ligado: boolean;
  readonly aoMudar: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={props.ligado}
        onChange={(e) => props.aoMudar(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      />
      <span className="flex flex-col">
        <span className="text-sm text-conteudo-primario">{props.rotulo}</span>
        <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
      </span>
    </label>
  );
}
